import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import {
  BaseBillingTermMilestone,
  BaseBillingTermRecurring,
  Billing,
  BillingPlanTerm,
  CandidateStatus,
  MinimalCandidateStatus,
  Payment,
  PaymentStatus,
} from "@/types";
import dateStringToLocalDate from "@/utils/dateStringToLocalDate";

interface ExpectedPayment {
  kind: "recurring" | "milestone";
  billingPlanTermId: string;

  // recurring only
  date?: string; // YYYY-MM-DD (or ISO)
  termIndex: number;
  iterationIndex?: number; // 0-based within that term

  currency: string;
  expectedTotalAmountCents: number;

  // for milestone only
  milestoneLabel?: string;
}

interface CompletedPayment {
  kind: "completed";
  paymentId: string;
  label: string; // date string or milestone label
  date?: string; // ISO (for recurring, optional)
  currency: string;
  totalAmountCents: number;
  taxCents: number;
  status: PaymentStatus;
  billingPlanTermId?: string;
}

interface UpcomingPayment {
  kind: "upcoming";
  label: string; // date string or milestone label
  date?: string; // ISO if date-based
  currency: string;
  expectedTotalAmountCents: number;
  billingPlanTermId?: string;
}

interface CompanyPaymentTimeline {
  completedPayments: CompletedPayment[];
  upcomingPayments: UpcomingPayment[];
}

interface ExpectedPayout {
  kind: "recurring" | "milestone";
  billingPlanTermId: string;

  // recurring only
  date?: string;
  termIndex: number;
  iterationIndex?: number;

  currency: string;
  expectedCandidateAmountCents: number;

  milestoneLabel?: string;
}

interface CandidateCompletedPayout {
  kind: "completed";
  payoutId: string;
  label: string;
  date?: string;
  currency: string;
  amountCents: number;
}

interface CandidateUpcomingPayout {
  kind: "upcoming";
  label: string;
  date?: string;
  currency: string;
  expectedAmountCents: number;
}

function formatIsoDateForLabel(iso: string): string {
  const d = dateStringToLocalDate(iso) ?? new Date(iso);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

function clampOpenEndedCount(
  term: BillingPlanTerm & { termType: "recurring" },
): number {
  return term.isOpenEnded ? 12 : (term.iterations ?? 0);
}

function getMilestoneLabel(
  billing: Billing,
  termId: string,
): string | undefined {
  const t = billing.terms.find((x) => x.id === termId);
  return t && t.termType === "milestone" ? t.milestoneLabel : undefined;
}

function daysInMonthUTC(year: number, monthIndex0: number): number {
  // monthIndex0: 0=Jan ... 11=Dec
  return new Date(Date.UTC(year, monthIndex0 + 1, 0)).getUTCDate();
}

function toIsoDateUTC(d: Date): string {
  // YYYY-MM-DD
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function buildExpectedPayments(billing: Billing): ExpectedPayment[] {
  const recurringTerms = billing.terms.filter(
    (t): t is BillingPlanTerm & BaseBillingTermRecurring =>
      t.termType === "recurring",
  );

  const milestoneTerms = billing.terms.filter(
    (t): t is BillingPlanTerm & BaseBillingTermMilestone =>
      t.termType === "milestone",
  );

  // Total number of recurring “slots” we want to project
  const recurringSlots = recurringTerms.reduce(
    (sum, t) => sum + clampOpenEndedCount(t),
    0,
  );

  // Project all dates for recurring slots from the global anchor
  const allDates = projectMonthlyDates({
    firstBillingDateIso: billing.firstBillingDate,
    count: recurringSlots,
    billingDay:
      recurringTerms[0]?.billingDay ??
      new Date(billing.firstBillingDate).getUTCDate(),
  });

  // Map dates to terms sequentially
  const expected: ExpectedPayment[] = [];
  let cursor = 0;

  let idx = 0;
  for (const term of recurringTerms) {
    const count = clampOpenEndedCount(term);
    for (let i = 0; i < count; i++) {
      expected.push({
        kind: "recurring",
        billingPlanTermId: term.id,
        termIndex: idx,
        iterationIndex: i,
        date: allDates[cursor],
        currency: term.currency,
        expectedTotalAmountCents: term.totalAmountCents,
      });
      cursor++;
    }
    idx++;
  }

  // Milestones are “expected”, but not date-based
  idx = 0;
  for (const term of milestoneTerms) {
    expected.push({
      kind: "milestone",
      billingPlanTermId: term.id,
      termIndex: idx,
      milestoneLabel: term.milestoneLabel,
      currency: term.currency,
      expectedTotalAmountCents: term.totalAmountCents,
    });
    idx++;
  }

  return expected;
}

function buildExpectedCandidatePayouts(billing: Billing): ExpectedPayout[] {
  const expectedPayments = buildExpectedPayments(billing);

  return expectedPayments.map((e) => ({
    kind: e.kind,
    billingPlanTermId: e.billingPlanTermId,
    termIndex: e.termIndex,
    iterationIndex: e.iterationIndex,
    date: e.date,
    milestoneLabel: e.milestoneLabel,
    currency: e.currency,
    expectedCandidateAmountCents:
      billing.terms.find((t) => t.id === e.billingPlanTermId)
        ?.candidateAmountCents ?? 0,
  }));
}

function projectMonthlyDates(params: {
  firstBillingDateIso: string; // can be YYYY-MM-DD or full ISO
  count: number;
  billingDay: number; // 1-31
}): string[] {
  const { firstBillingDateIso, count, billingDay } = params;

  const start =
    dateStringToLocalDate(firstBillingDateIso) ?? new Date(firstBillingDateIso);
  // anchor month/year from firstBillingDate
  const startYear = start.getUTCFullYear();
  const startMonth = start.getUTCMonth(); // 0-11

  const dates: string[] = [];

  for (let i = 0; i < count; i++) {
    const month = startMonth + i;
    const year = startYear + Math.floor(month / 12);
    const monthIndex0 = ((month % 12) + 12) % 12;

    const dim = daysInMonthUTC(year, monthIndex0);
    const day = Math.min(Math.max(1, billingDay), dim);

    const d = new Date(Date.UTC(year, monthIndex0, day));
    dates.push(toIsoDateUTC(d));
  }

  return dates;
}

function buildCompanyPaymentTimeline(
  billing: Billing,
  payments: Payment[],
): CompanyPaymentTimeline {
  const expected = buildExpectedPayments(billing);

  // Completed events shown in history (you already do this)
  const completedPayments: CompletedPayment[] = payments
    .filter((p) =>
      ["paid", "needs_review", "refunded", "void", "uncollectible"].includes(
        p.status,
      ),
    )
    .sort((a, b) => {
      const aPaid = !!a.paidAt;
      const bPaid = !!b.paidAt;
      if (aPaid !== bPaid) return aPaid ? -1 : 1;
      if (aPaid && bPaid) return a.paidAt!.localeCompare(b.paidAt!);
      return 0;
    })
    .map((p) => ({
      kind: "completed",
      paymentId: p.id,
      label: p.billingPlanTermId
        ? (getMilestoneLabel(billing, p.billingPlanTermId) ??
          "Milestone payment")
        : p.paidAt
          ? `paid on ${formatIsoDateForLabel(p.paidAt)}`
          : "paid (date unknown)",
      date: p.paidAt,
      currency: p.currency,
      totalAmountCents: p.totalAmountCents,
      taxCents: p.taxCents,
      status: p.status,
      billingPlanTermId: p.billingPlanTermId,
    }));

  // Determine which expected items are still upcoming
  const paidMilestoneIds = new Set(
    payments
      .filter((p) => p.status === "paid" && p.billingPlanTermId)
      .map((p) => p.billingPlanTermId!),
  );

  const paidRecurringCount = payments.filter(
    (p) => p.status === "paid" && !p.billingPlanTermId,
  ).length;

  let recurringConsumed = 0;

  const upcomingPayments: UpcomingPayment[] = [];

  for (const e of expected) {
    if (e.kind === "milestone") {
      if (paidMilestoneIds.has(e.billingPlanTermId)) continue;

      upcomingPayments.push({
        kind: "upcoming",
        label: e.milestoneLabel ?? "Milestone",
        billingPlanTermId: e.billingPlanTermId,
        currency: e.currency,
        expectedTotalAmountCents: e.expectedTotalAmountCents,
      });

      continue;
    }

    // recurring
    if (recurringConsumed < paidRecurringCount) {
      recurringConsumed++;
      continue;
    }

    upcomingPayments.push({
      kind: "upcoming",
      label: e.date
        ? `due on ${formatIsoDateForLabel(e.date)}`
        : "upcoming payment",
      date: e.date,
      currency: e.currency,
      expectedTotalAmountCents: e.expectedTotalAmountCents,
    });
  }

  // Keep upcoming in chronological order: dates first, then milestones
  upcomingPayments.sort((a, b) => {
    const aHasDate = !!a.date;
    const bHasDate = !!b.date;
    if (aHasDate && bHasDate) return a.date!.localeCompare(b.date!);
    if (aHasDate !== bHasDate) return aHasDate ? -1 : 1;
    return 0; // milestones already in term_index order due to buildExpectedPayments
  });

  return { completedPayments, upcomingPayments };
}

function buildCandidateCompletedUpcoming(
  billing: Billing,
  payments: Payment[],
): {
  completedPayouts: CandidateCompletedPayout[];
  upcomingPayouts: CandidateUpcomingPayout[];
} {
  const expected = buildExpectedCandidatePayouts(billing);

  // Completed milestone payouts
  const paidMilestoneTermIds = new Set(
    payments
      .filter((p) => p.billingPlanTermId && p.payout?.status === "succeeded")
      .map((p) => p.billingPlanTermId),
  );

  // Count recurring payouts completed
  const recurringSucceededCount = payments.filter(
    (p) => !p.billingPlanTermId && p.payout?.status === "succeeded",
  ).length;

  // Build completed list (chronological)
  const completedPayouts: CandidateCompletedPayout[] = payments
    .filter((p) => p.payout?.status === "succeeded")
    .sort((a, b) => {
      const aDate = a.payout?.lastAttemptAt;
      const bDate = b.payout?.lastAttemptAt;
      if (aDate && bDate) return aDate.localeCompare(bDate);
      if (aDate) return -1;
      if (bDate) return 1;
      return 0;
    })
    .map((p) => ({
      kind: "completed",
      payoutId: p.payout!.id,
      label: p.billingPlanTermId
        ? (getMilestoneLabel(billing, p.billingPlanTermId) ??
          "Milestone payout")
        : p.payout?.lastAttemptAt
          ? `paid on ${formatIsoDateForLabel(p.payout.lastAttemptAt)}`
          : "paid",
      date: p.payout?.lastAttemptAt,
      currency: p.currency,
      amountCents: p.payout!.amountCents,
    }));

  // Build upcoming list
  let recurringConsumed = 0;
  const upcomingPayouts: CandidateUpcomingPayout[] = [];

  for (const e of expected) {
    if (e.kind === "milestone") {
      if (paidMilestoneTermIds.has(e.billingPlanTermId)) continue;

      upcomingPayouts.push({
        kind: "upcoming",
        label: e.milestoneLabel ?? "Milestone",
        currency: e.currency,
        expectedAmountCents: e.expectedCandidateAmountCents,
      });
      continue;
    }

    // recurring
    if (recurringConsumed < recurringSucceededCount) {
      recurringConsumed++;
      continue;
    }

    upcomingPayouts.push({
      kind: "upcoming",
      label: e.date
        ? `expected on ${formatIsoDateForLabel(e.date)}`
        : "expected",
      date: e.date,
      currency: e.currency,
      expectedAmountCents: e.expectedCandidateAmountCents,
    });
  }

  upcomingPayouts.sort((a, b) => {
    const aHasDate = !!a.date;
    const bHasDate = !!b.date;
    if (aHasDate && bHasDate) return a.date!.localeCompare(b.date!);
    if (aHasDate !== bHasDate) return aHasDate ? -1 : 1;
    return 0;
  });

  return { completedPayouts, upcomingPayouts };
}

const ViewPaymentsCompany = ({
  candidate,
}: {
  candidate: CandidateStatus | MinimalCandidateStatus;
}) => {
  const [open, setOpen] = useState(false);

  const { billing, payments } = candidate;

  if (!billing) {
    return;
  }

  const engagementType = billing.terms[0]?.termType;

  if (
    engagementType === "recurring" &&
    billing.companyCheckoutStatus !== "completed" &&
    billing.companyCheckoutStatus !== "active" &&
    billing.companyCheckoutStatus !== "cancelled"
  ) {
    return;
  }

  const { completedPayments, upcomingPayments } = buildCompanyPaymentTimeline(
    billing,
    payments,
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">View Payments</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle className="sr-only">
          Completed and Upcoming Payments
        </DialogTitle>
        {completedPayments.length ? (
          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-bold text-gray-900">
              Completed Payments
            </h3>
            <div className="space-y-2 text-gray-700">
              {completedPayments.map((payment) => (
                <p key={`payment-${payment.paymentId}`}>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: payment.currency,
                  }).format(payment.totalAmountCents / 100)}{" "}
                  {payment.label}
                </p>
              ))}
            </div>
          </div>
        ) : null}
        {upcomingPayments.length ? (
          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-bold text-gray-900">
              Upcoming Payments
            </h3>
            <div className="space-y-2 text-gray-700">
              {upcomingPayments.map((payment, idx) => (
                <p key={`upcoming-payment-${idx}`}>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: payment.currency,
                  }).format(payment.expectedTotalAmountCents / 100)}
                  {" + tax "}
                  {payment.label}
                </p>
              ))}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const ViewPaymentsCandidate = ({
  candidate,
}: {
  candidate: CandidateStatus | MinimalCandidateStatus;
}) => {
  const [open, setOpen] = useState(false);

  const { billing, payments } = candidate;

  if (!billing) {
    return;
  }

  const engagementType = billing.terms[0]?.termType;

  if (
    engagementType === "recurring" &&
    billing.companyCheckoutStatus !== "completed" &&
    billing.companyCheckoutStatus !== "active" &&
    billing.companyCheckoutStatus !== "cancelled"
  ) {
    return;
  }

  const { completedPayouts, upcomingPayouts } = buildCandidateCompletedUpcoming(
    billing,
    payments,
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="secondary">View Payments</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogTitle className="sr-only">
          Completed and Upcoming Payments
        </DialogTitle>
        {completedPayouts.length ? (
          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-bold text-gray-900">
              Completed Payments
            </h3>
            <div className="space-y-2 text-gray-700">
              {completedPayouts.map((payout) => (
                <p key={`payout-${payout.payoutId}`}>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: payout.currency,
                  }).format(payout.amountCents / 100)}{" "}
                  {payout.label}
                </p>
              ))}
            </div>
          </div>
        ) : null}
        {upcomingPayouts.length ? (
          <div className="flex flex-col gap-3">
            <h3 className="text-lg font-bold text-gray-900">
              Upcoming Payments
            </h3>
            <div className="space-y-2 text-gray-700">
              {upcomingPayouts.map((payout, idx) => (
                <p key={`upcoming-payouts-${idx}`}>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: payout.currency,
                  }).format(payout.expectedAmountCents / 100)}{" "}
                  {payout.label}
                </p>
              ))}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export { ViewPaymentsCompany, ViewPaymentsCandidate };
